import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  Signal,
  TemplateRef,
  untracked,
  viewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { AuthFacade } from '@iot-platform/auth';
import { FavoriteViewsService } from '@iot-platform/data-access/favorite-views';
import { FormControlHintType, FormControlType, FormField, FormGroupComponent } from '@iot-platform/iot-platform-ui';
import { FavoriteView } from '@iot-platform/models/common';
import { GRID_TYPE_LABELS, GRID_TYPES, GridTypes, I4BGrid, I4BGridData, I4BGridOptions } from '@iot-platform/models/grid-engine';
import { TranslatePipe } from '@ngx-translate/core';
import { isEqual, mapValues } from 'lodash';
import { combineLatest, filter, switchMap } from 'rxjs';
import { FavoriteViewFormControlNames } from '../../../models';

@Component({
  selector: 'iot-platform-ui-favorite-view-grid-form',
  imports: [FormGroupComponent, TranslatePipe, MatRadioModule, FormsModule],
  templateUrl: './grid-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteViewGridFormComponent {
  favoriteView = input<FavoriteView>();
  masterView = input<string>();
  businessProfileId = input<string>();
  shared = input<boolean>(false);
  formValue = output<Partial<FavoriteView> | null>();
  selectedGrid = output<I4BGrid<I4BGridOptions, I4BGridData> | null>();
  formStateChanged = output<boolean>();
  form: FormGroup = new FormGroup({});
  initialFormState: WritableSignal<unknown> = signal(null);
  GridTypes = GridTypes;
  GRID_TYPES = GRID_TYPES;
  gridTypeLabels = GRID_TYPE_LABELS;
  gridType = signal<GridTypes>(GridTypes.USER_GRID);
  // Sentinel (not null): mat-select treats a null option as "reset" and would display an empty trigger.
  readonly DEFAULT_GRID_OPTION = { id: null, name: '' } as unknown as I4BGrid<I4BGridOptions, I4BGridData>;
  gridsLoaded = computed(() => this.grids() !== undefined);
  userGrids = computed(() => this.grids()?.filter((grid) => !grid.businessProfileId && !!grid.userId && (!this.shared() || !grid.isDefault)) ?? []);
  sharedGrids = computed(() => this.grids()?.filter((grid) => !!grid.businessProfileId) ?? []);
  typedGrids = computed(() => (this.gridType() === GridTypes.USER_GRID ? this.userGrids() : this.sharedGrids()));
  gridItems = computed(() => [this.DEFAULT_GRID_OPTION, ...this.typedGrids()]);
  gridOptionTpl = viewChild<TemplateRef<{ item: I4BGrid<I4BGridOptions, I4BGridData> }>>('gridOptionTpl');
  initFormEffect = effect(() => {
    this.favoriteView();
    untracked(() => {
      this.initForm();
      this.initFormInitialState();
    });
  });
  initGridEffect = effect(() => {
    const gridId = this.favoriteView()?.gridId;
    if (!this.gridsLoaded()) {
      return;
    }
    untracked(() => {
      const grid = this.grids()?.find((g) => g.id === gridId);
      this.gridType.set(grid?.businessProfileId ? GridTypes.SHARED_GRIDS : GridTypes.USER_GRID);
      this.grid.patchValue(grid ?? this.DEFAULT_GRID_OPTION);
      this.initFormInitialState();
    });
  });
  reconcileGridEffect = effect(() => {
    const items = this.gridItems();
    if (!this.gridsLoaded()) {
      return;
    }
    untracked(() => {
      const id = this.grid?.getRawValue()?.id ?? null;
      if (!items.some((g) => g.id === id)) {
        this.grid.patchValue(this.DEFAULT_GRID_OPTION);
      }
    });
  });
  fields: WritableSignal<Partial<FormField>[]> = signal([
    {
      type: FormControlType.DROP_DOWN_LIST,
      name: signal(FavoriteViewFormControlNames.GRID),
      label: signal('FAVORITE_VIEW.FORM.SELECT_A_GRID'),
      fxFlex: signal('100%'),
      items: this.gridItems,
      showSpinner: computed(() => !this.gridsLoaded()),
      disabled: computed(() => this.typedGrids().length === 0),
      hint: computed(() =>
        this.typedGrids().length === 0 ? { type: signal(FormControlHintType.TEXT), text: signal('IOT_DICTIONARY.NO_GRID_AVAILABLE') } : undefined
      ),
      displayBy: (item: I4BGrid<I4BGridOptions, I4BGridData>) => item?.name ?? '',
      compareFn: (a: I4BGrid<I4BGridOptions, I4BGridData>, b: I4BGrid<I4BGridOptions, I4BGridData>) => a?.id === b?.id,
      selectDisplayWrapperRef: this.gridOptionTpl,
      selectTriggerRef: this.gridOptionTpl
    }
  ]);
  formValid: Signal<boolean> = computed(() => {
    this.formChanges();
    const gridId = this.grid?.getRawValue()?.id ?? null;
    return this.gridsLoaded() && this.gridItems().some((g) => g.id === gridId);
  });
  formValueChanged: Signal<boolean> = computed(() => {
    this.formChanges();
    const initialFormState = this.initialFormState();
    const currentFormState = mapValues(this.form.getRawValue(), (v) => (v === '' ? null : v));
    return !isEqual(currentFormState, initialFormState);
  });
  emitFormValueEventEffect = effect(() => {
    this.formChanges();
    const formValid = this.formValid();
    const formValueChanged = this.formValueChanged();
    const value = this.grid.getRawValue() as I4BGrid<I4BGridOptions, I4BGridData> | null;
    this.formStateChanged.emit(formValueChanged);
    this.selectedGrid.emit(value && value !== this.DEFAULT_GRID_OPTION ? value : null);
    if (formValid) {
      this.formValue.emit({ gridId: value?.id ?? null });
    } else {
      this.formValue.emit(null);
    }
  });
  private readonly favoriteViewsService: FavoriteViewsService = inject(FavoriteViewsService);
  private readonly authFacade: AuthFacade = inject(AuthFacade);
  userId = this.authFacade.userId;
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  formChanges: Signal<unknown> = toSignal(this.form.events.pipe(takeUntilDestroyed(this.destroyRef)));
  grids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[] | undefined> = toSignal(
    combineLatest([toObservable(this.masterView), toObservable(this.businessProfileId), toObservable(this.userId)]).pipe(
      filter(([_, businessProfileId, __]) => !!businessProfileId),
      switchMap(([masterView, businessProfileId, userId]) => this.favoriteViewsService.getGrids(masterView, businessProfileId, userId)),
      takeUntilDestroyed(this.destroyRef)
    ),
    { initialValue: undefined }
  );

  get grid(): AbstractControl {
    return this.form.get(FavoriteViewFormControlNames.GRID);
  }

  onGridTypeChange(type: GridTypes): void {
    const original = this.grids()?.find((g) => g.id === this.favoriteView()?.gridId);
    const originalType = original?.businessProfileId ? GridTypes.SHARED_GRIDS : GridTypes.USER_GRID;
    this.grid.patchValue(original && type === originalType ? original : this.DEFAULT_GRID_OPTION);
  }

  private initForm(): void {
    this.form.addControl(FavoriteViewFormControlNames.GRID, new FormControl<I4BGrid<I4BGridOptions, I4BGridData>>(this.DEFAULT_GRID_OPTION));
  }

  private initFormInitialState(): void {
    this.initialFormState.set(this.form.getRawValue());
  }
}
